import { useState } from 'react';
import { useSimulatorStore } from './stores/useSimulatorStore';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useWorkspacePersistence } from './hooks/useWorkspacePersistence';
import { useIsMobile } from './hooks/useIsMobile';
import type { TabId } from './types';
import {
  BarChart3,
  DollarSign,
  Layers,
  PieChart,
  TrendingUp,
  RotateCcw,
  LogOut,
  Sparkles,
  LayoutDashboard,
  Gem,
  HelpCircle,
  MessageSquareQuote,
  Megaphone,
  Shield,
} from 'lucide-react';
import { ExecutiveSummaryTab } from './components/summary/ExecutiveSummaryTab';
import { PackageBuilderTab } from './components/builder/PackageBuilderTab';
import { PriceExplorerTab } from './components/price/PriceExplorerTab';
import { FeatureValueTab } from './components/features/FeatureValueTab';
import { SegmentDeepDiveTab } from './components/segments/SegmentDeepDiveTab';
import { FinancialModelTab } from './components/financial/FinancialModelTab';
import { MarketingStrategyTab } from './components/marketing/MarketingStrategyTab';
import { VoiceOfMarketTab } from './components/verbatims/VoiceOfMarketTab';
import { HelpGuidePage } from './components/help/HelpGuidePage';
import { LoginPage } from './components/auth/LoginPage';
import { AdminPanel } from './components/admin/AdminPanel';
import { AssumptionsDrawer } from './components/layout/AssumptionsDrawer';
import { AssumptionsStrip } from './components/layout/AssumptionsStrip';
import { ChatPanel } from './components/ai/ChatPanel';
import { ChatPanelContent } from './components/ai/ChatPanelContent';
import { InsightPreloader } from './components/ai/InsightPreloader';
import { MobileNav } from './components/layout/MobileNav';
import { ErrorBoundary } from './components/common/ErrorBoundary';

const BASE_TABS: { id: TabId; label: string; icon: typeof BarChart3 }[] = [
  { id: 'summary',      label: 'Market',       icon: LayoutDashboard },
  { id: 'builder',      label: 'Packages',     icon: Layers },
  { id: 'featureValue', label: 'Feature Value', icon: Gem },
  { id: 'price',        label: 'Pricing',      icon: DollarSign },
  { id: 'verbatims',   label: 'Voice of Market', icon: MessageSquareQuote },
  { id: 'segments',     label: 'Segments',     icon: PieChart },
  { id: 'financial',    label: 'Financials',   icon: TrendingUp },
  { id: 'marketing',   label: 'Marketing',    icon: Megaphone },
];

function AppContent() {
  const { user, loading, logout } = useAuth();
  const [showHelp, setShowHelp] = useState(false);

  const isMobile = useIsMobile();
  const activeTab = useSimulatorStore(s => s.activeTab);
  const setActiveTab = useSimulatorStore(s => s.setActiveTab);
  const resetToDefaults = useSimulatorStore(s => s.resetToDefaults);
  const toggleChat = useSimulatorStore(s => s.toggleChatPanel);
  const chatPanelOpen = useSimulatorStore(s => s.chatPanelOpen);
  const setChatPanelOpen = useSimulatorStore(s => s.setChatPanelOpen);
  const setLastTabBeforeChat = useSimulatorStore(s => s.setLastTabBeforeChat);

  useWorkspacePersistence();

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="relative inline-block w-10 h-10 mb-5">
            <div className="absolute inset-0 border-2 border-chart-teal/15 rounded-full" />
            <div className="absolute inset-0 border-2 border-transparent border-t-chart-teal rounded-full animate-spin" />
          </div>
          <p className="text-sm text-secondary font-medium">Loading configurator...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const tabs = user.role === 'super_admin'
    ? [...BASE_TABS, { id: 'admin' as TabId, label: 'Admin', icon: Shield }]
    : BASE_TABS;

  const handleLogout = async () => {
    await logout();
  };

  const handleLogoClick = () => {
    setShowHelp(false);
    setActiveTab('summary');
  };

  const iconBtnClass = "p-2 rounded-lg transition-all text-secondary hover:text-primary border border-border hover:bg-surface";

  return (
    <div className={`min-h-screen ${isMobile && chatPanelOpen ? 'h-[100dvh] flex flex-col overflow-hidden' : ''}`} style={{ backgroundColor: 'var(--color-canvas)' }}>
      <header
        className="sticky top-0 z-50 shrink-0"
        style={{
          backgroundColor: 'var(--color-header-bg)',
          borderBottom: '1px solid var(--color-header-border)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        }}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-3">
          <div className="flex items-center justify-between">
            <button onClick={handleLogoClick} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/alterme-logo.png" alt="AlterMe" className="h-[22px] sm:h-7 w-auto" style={{ mixBlendMode: 'multiply' }} />
              <div className="text-left">
                <span className="text-xs sm:text-sm font-medium text-muted tracking-wide uppercase">Product Configurator</span>
              </div>
            </button>

            <div className="flex items-center gap-2">
              <span className="text-xs text-muted hidden lg:inline mr-1">{user.email}</span>

              <button
                onClick={toggleChat}
                className="hidden md:flex items-center gap-2 px-3.5 py-2 text-sm rounded-xl transition-all border border-chart-teal/25 text-chart-teal hover:bg-chart-teal/8 hover:border-chart-teal/40 hover:shadow-glow-teal"
              >
                <Sparkles size={14} />
                <span className="font-semibold">AI Analyst</span>
              </button>

              <button onClick={resetToDefaults} className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all text-secondary hover:text-primary border border-border hover:bg-surface">
                <RotateCcw size={14} />
                <span>Reset</span>
              </button>

              <button onClick={() => setShowHelp(true)} className={iconBtnClass} aria-label="Help guide">
                <HelpCircle size={16} />
              </button>

              <button onClick={handleLogout} className="p-2 rounded-lg transition-all text-secondary hover:text-metric-negative border border-border hover:bg-surface" aria-label="Log out">
                <LogOut size={16} />
              </button>
            </div>
          </div>

          {!showHelp && (
            <nav className="hidden md:flex gap-0.5 mt-3 -mb-px">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium rounded-t-lg transition-all whitespace-nowrap ${
                      isActive
                        ? 'text-chart-teal'
                        : 'text-secondary hover:text-primary hover:bg-hover-bg'
                    }`}
                    style={isActive ? {
                      backgroundColor: 'var(--color-canvas)',
                      border: '1px solid var(--color-header-border)',
                      borderBottomColor: 'var(--color-canvas)',
                    } : undefined}
                  >
                    <Icon size={15} className={isActive ? 'text-chart-teal' : ''} />
                    {tab.label}
                    {isActive && (
                      <span className="absolute top-1 right-1.5 w-1 h-1 rounded-full bg-chart-teal live-dot" />
                    )}
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        {!showHelp && <AssumptionsStrip />}
      </header>

      {showHelp ? (
        <main className="max-w-[1600px] mx-auto">
          <HelpGuidePage onClose={() => setShowHelp(false)} />
        </main>
      ) : isMobile && chatPanelOpen ? (
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <ChatPanelContent
            onClose={() => {
              const tab = useSimulatorStore.getState().lastTabBeforeChat;
              setChatPanelOpen(false);
              if (tab) setActiveTab(tab);
              setLastTabBeforeChat(null);
            }}
            showBack
          />
        </main>
      ) : (
        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 py-6 md:py-8 pb-24 md:pb-8">
          <ErrorBoundary>
            {activeTab === 'summary' && <ExecutiveSummaryTab />}
            {activeTab === 'builder' && <PackageBuilderTab />}
            {activeTab === 'featureValue' && <FeatureValueTab />}
            {activeTab === 'price' && <PriceExplorerTab />}
            {activeTab === 'verbatims' && <VoiceOfMarketTab />}
            {activeTab === 'segments' && <SegmentDeepDiveTab />}
            {activeTab === 'financial' && <FinancialModelTab />}
            {activeTab === 'marketing' && <MarketingStrategyTab />}
            {activeTab === 'admin' && user.role === 'super_admin' && <AdminPanel />}
          </ErrorBoundary>
        </main>
      )}

      {!showHelp && (
        <footer className="hidden md:flex items-center justify-center gap-2 py-5 border-t" style={{ borderColor: 'var(--color-border-light)' }}>
          <img src="/angry-gods-logo.png" alt="Angry Gods" className="w-4 h-4 rounded opacity-60" />
          <p className="text-micro text-muted tracking-wide">
            Fielded, designed and developed by{' '}
            <a href="https://www.angrygods.com" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-chart-teal transition-colors font-medium">
              Angry Gods
            </a>
          </p>
        </footer>
      )}

      <AssumptionsDrawer />
      <ChatPanel />
      {!showHelp && <MobileNav />}
      <InsightPreloader />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
